/**
 * hud.ts — Heads-up display as a DOM overlay.
 *
 * Replaces the old Phaser UIScene. It never touches gameplay objects directly — it
 * only subscribes to the game's events (types.ts → GameEvents) and redraws. A DOM
 * overlay is the natural fit now that rendering is WebGL: it stays crisp at any
 * resolution, costs no draw calls, and is where touch controls would be added for
 * the Capacitor build.
 */
import { FRENZY } from '../config/constants';
import type { TutorialCard, TutorialStepId, TutorialTipId } from '../systems/tutorial';
import { activeLevel } from '../config/level';
import { homeDoorstep } from '../systems/home';
import { computeEdgeArrow, formatDistance } from '../systems/navigation';
import type { Vec2 } from '../utils/vec2';
import {
  DogState,
  Objective,
  GameEvents,
  type Emitter,
  type InputScheme,
  type LossReason,
} from '../types';

/** Everything the HUD needs that isn't an event. */
export interface HudOptions {
  /** e.g. `Level 4 · Medium`. Shown under the dog state; omitted if absent. */
  levelLabel?: string;
  /**
   * Called to abandon the run and go back to the level picker.
   *
   * Passed in rather than assigned afterwards because the button only exists
   * when there is somewhere to go back *to* — a build with no menu shouldn't
   * render a control that does nothing.
   */
  onMenu?: () => void;
  /** Called to advance to the next level when winning a level. */
  onNextLevel?: () => void;
  /** Difficulty label of the next level (e.g. `Medium`). Included in the Next Level button. */
  nextLevelDifficulty?: string;
  /** Sound manager handle for toggling mute from the HUD. */
  soundManager?: { isMuted: boolean; toggleMute: () => boolean };
}

/**
 * Player-facing instruction for each phase, worded per control scheme.
 *
 * Which one shows follows what the player is *actually using* (GameEvents.
 * INPUT_SCHEME), not a one-time guess about the device — otherwise the hints keep
 * naming keys nobody is pressing on a hybrid machine, or after someone picks up
 * a mouse. Both schemes always work regardless of the wording.
 */
const OBJECTIVE_TEXT: Record<Objective, Record<InputScheme, string>> = {
  [Objective.WALKING]: {
    keys: 'Walk the dog (WASD / Arrows). Watch for critters and cars!',
    pointer: 'Drag anywhere to walk. Watch for critters and cars!',
  },
  [Objective.CARRYING_BAG]: {
    keys: 'Bag in hand — take it to the bin (glowing ring).',
    pointer: 'Bag in hand — take it to the bin (glowing ring).',
  },
  [Objective.GO_HOME]: {
    keys: 'All clean! Head to the home zone to finish.',
    pointer: 'All clean! Head to the home zone to finish.',
  },
  [Objective.WON]: { keys: '', pointer: '' },
};

/**
 * What the top line says through the whole errand at the park — the urge
 * building, the squat, and the walker bagging it up.
 *
 * The objective is `WALKING` for all of it — the objective chain doesn't move
 * until the bag is in hand — so without these the instruction would sit there
 * saying "walk the dog" through the one part of the walk that genuinely needs
 * steering, while the only mention of it was a small line underneath. These
 * take the line over instead, and the first two name the ring on the nearest
 * green, because "find some grass" is not useful advice in a town where most of
 * the parks are behind a building.
 *
 * Worded identically for both control schemes: none of them name a control,
 * and since the cleanup hold went there is no control left to name. The line
 * that used to sit here for it came in two versions — one naming the key, one
 * naming the on-screen button, plus a third for standing too far away.
 */
const URGE_TEXT: Partial<Record<DogState, string>> = {
  [DogState.SNIFFING]: 'The dog is sniffing about — it needs the grass soon. Head for the ring!',
  [DogState.NEEDS_GRASS]: 'The dog needs to go! Get it to the grass inside the ring.',
  [DogState.POOPING]: 'On the grass at last — let the dog finish.',
  [DogState.POOP_PENDING]: 'Good dog. Bagging it up…',
};

/**
 * The guided walk, lesson by lesson.
 *
 * The script itself — what each lesson is finished by, and in what order — is
 * the pure `systems/tutorial.ts`; this is only the wording, which is why it
 * lives here with every other line the player reads and gets re-worded for the
 * control scheme in exactly the same way the objective line does.
 *
 * Each card says the *why* as well as the *what*. "Drag anywhere" is a control;
 * "the dog only goes on grass" is the reason there is a ring on the map, and a
 * player who is told it once never has to be told again.
 *
 * ## The one card that names a control names both of them
 *
 * Elsewhere in the HUD a hint names only the scheme in use, and a wrong guess
 * costs nothing: the player is already walking, and the wording corrects itself
 * the moment they touch the other control. **Neither of those is true of the
 * opening tutorial card.** It is read *before* the player has touched anything,
 * so the device guess is all there is — and on a hybrid (a touchscreen laptop
 * reports `pointer: fine`) that guess is "keys", which on a machine being used
 * by touch is an instruction to press keys that may not exist, with no way to
 * discover the one that works. The card telling you how to move is the one card
 * that cannot afford to be wrong.
 *
 * So `walk` names **both**, led by whichever scheme the player looks to be
 * using. Both are always true — the stick works on a desktop and a keyboard
 * works on a tablet — and the order still corrects itself live when the player
 * picks up the other one. It is now the *only* card that names a control at
 * all: the cleanup card named a key and a button that no longer exist, and
 * steering is the whole of what a player has to be taught to press.
 */
const TUTORIAL_TEXT: Record<TutorialStepId, Record<InputScheme, string>> = {
  intro: {
    keys: 'This is your dog, on an elastic lead. One walk: let it do its business, take the bag to a bin, and be home.',
    pointer:
      'This is your dog, on an elastic lead. One walk: let it do its business, take the bag to a bin, and be home.',
  },
  walk: {
    keys: 'Walk with WASD or the arrow keys. Dragging anywhere on screen works too.',
    pointer: 'Drag anywhere on screen to walk. WASD and the arrow keys work too.',
  },
  lead: {
    keys: 'The lead is a spring — the dog can slow you and pull you off line, but never stop you. Keep walking.',
    pointer:
      'The lead is a spring — the dog can slow you and pull you off line, but never stop you. Keep walking.',
  },
  critters: {
    keys: 'There goes a critter. Let the dog get near one and it locks on, barking and hauling — the Frenzy bar top right fills, and a full bar means the dog is gone. Give them room.',
    pointer:
      'There goes a critter. Let the dog get near one and it locks on, barking and hauling — the Frenzy bar top right fills, and a full bar means the dog is gone. Give them room.',
  },
  urge: {
    keys: 'Watch the dog. When it starts sniffing about it needs the grass, and a ring marks the nearest green.',
    pointer:
      'Watch the dog. When it starts sniffing about it needs the grass, and a ring marks the nearest green.',
  },
  grass: {
    keys: 'A dog only goes on grass. Walk it inside the ring and let it finish — you bag it up on the spot, so there is nothing to press.',
    pointer:
      'A dog only goes on grass. Walk it inside the ring and let it finish — you bag it up on the spot, so there is nothing to press.',
  },
  bin: {
    keys: 'Bag in hand. Follow the yellow ring to the bin — and mind the road: a car touching either of you ends the walk, so wait at the kerb and let it pass.',
    pointer:
      'Bag in hand. Follow the yellow ring to the bin — and mind the road: a car touching either of you ends the walk, so wait at the kerb and let it pass.',
  },
  home: {
    keys: 'Last leg: walk back to your own front door — the green ring — to finish the walk.',
    pointer: 'Last leg: walk back to your own front door — the green ring — to finish the walk.',
  },
};

/**
 * The asides, shown once each when the world produces the thing they are about.
 *
 * Both name a meter or a sound the player can already perceive, because the
 * point of an aside is to explain something that just happened rather than to
 * warn about something that might.
 */
const TUTORIAL_TIP_TEXT: Record<TutorialTipId, string> = {
  traffic: '🚗 That horn means a car is bearing down on you — one touch on either of you ends the walk.',
  critter: '🐿️ The dog has locked on. The frenzy meter fills while it lunges; walk away and it empties.',
};

/** Restart prompt, worded for whichever control the player is using. */
const RESTART_HINT: Record<InputScheme, string> = {
  keys: 'Tap or click anywhere to',
  pointer: 'Tap anywhere to',
};

/** Heading + subtitle for each way a run can end badly. */
const LOSS_TEXT: Record<LossReason, { title: string; hint: string }> = {
  car: { title: '🚗 Watch out for cars!', hint: 'try again' },
  frenzy: { title: '🐕‍🦺 The dog got away!', hint: 'try again' },
};

/** Friendly label + colour for each dog state. */
const DOG_STATE_INFO: Record<DogState, { label: string; color: string }> = {
  [DogState.CALM]: { label: 'Calm', color: '#bfe8c8' },
  [DogState.DISTRACTED]: { label: 'Distracted!', color: '#ffe08a' },
  [DogState.HYPER_PULL]: { label: 'BARKING — PULLING!', color: '#ff8a8a' },
  [DogState.SNIFFING]: { label: 'Sniffing about — needs to go soon…', color: '#e0b070' },
  [DogState.NEEDS_GRASS]: { label: 'Needs the grass!', color: '#e0b070' },
  [DogState.POOPING]: { label: 'Going… hold on', color: '#e0b070' },
  [DogState.POOP_PENDING]: { label: 'Done — bagging it up', color: '#e0b070' },
  [DogState.CLEANED]: { label: 'Happy', color: '#bfe8c8' },
};

const CSS = `
.hud{position:fixed;inset:0;pointer-events:none;font-family:system-ui,Arial,sans-serif;
  color:#fff;text-shadow:0 1px 3px rgba(0,0,0,.8);z-index:10}
/* The top strip: the objective block, and under it the guided walk's card.
   One anchored column in normal flow, so the card is placed by the block above
   it rather than by a number somebody has to keep in step with how many lines
   the objective happens to wrap to. */
.hud-top{position:absolute;top:16px;left:16px;right:16px;
  display:flex;flex-direction:column;align-items:flex-start;gap:9px}
/* Objective and dog state stack in normal flow inside one anchored box. They used
   to be absolutely positioned at fixed tops, which collided as soon as the
   objective wrapped to a second line on a narrow screen. */
/* The min-height is what keeps the card below this block clear of the frenzy
   meter, which is absolutely positioned over the same strip (top:52px, ~26 tall
   — so 62 below the strip's own top). Without it, a one-line objective leaves
   the card starting level with the meter and running under it on a phone. */
.hud-topleft{max-width:58vw;min-height:64px;display:flex;flex-direction:column;gap:4px}
.hud-objective{font-size:18px;font-weight:600}
/* The run-up to the poop is the one phase with no marker on the objective chain
   and a few seconds to act in, so the line changes colour as well as wording —
   an instruction that only swaps its words is easy to walk straight past. */
.hud-objective.is-urgent{color:#ffd79a}
.hud-dog{font-size:15px}
.hud-level{font-size:13px;opacity:.72;letter-spacing:.3px}
.hud-topright{position:absolute;top:14px;right:16px;display:flex;align-items:center;gap:10px}
.hud-sound-btn{width:32px;height:32px;background:rgba(0,0,0,.45);border:none;border-radius:16px;
  color:#fff;font-size:16px;cursor:pointer;display:flex;align-items:center;justify-content:center;
  pointer-events:auto;user-select:none;transition:transform .1s,background .1s}
.hud-sound-btn:hover{background:rgba(0,0,0,.65);transform:scale(1.08)}
.hud-sound-btn:active{transform:scale(0.95)}
.hud-menu-btn{pointer-events:auto;
  border:0;border-radius:8px;padding:5px 10px;font:inherit;font-size:12px;
  color:#fff;background:rgba(0,0,0,.42);cursor:pointer;display:none}
.hud-menu-btn:hover,.hud-menu-btn:focus-visible{background:rgba(0,0,0,.62);outline:none}
.hud-frenzy-wrap{position:absolute;top:52px;right:16px;width:160px}
.hud-label{font-size:11px;text-align:right;opacity:.85;margin-top:3px}
.hud-bar{height:10px;background:rgba(0,0,0,.45);border-radius:5px;overflow:hidden}
.hud-bar>i{display:block;height:100%;width:0;border-radius:5px;transition:width .08s linear}
/* A full bar ends the run, so past the warning mark it stops being scenery and
   starts flashing. Animating the wrapper rather than the fill leaves the width
   transition above alone. */
.hud-frenzy-wrap.is-warning{animation:hud-pulse .6s ease-in-out infinite}
.hud-frenzy-wrap.is-warning .hud-label{opacity:1;font-weight:700}
@keyframes hud-pulse{0%,100%{opacity:1}50%{opacity:.45}}
@media (prefers-reduced-motion:reduce){.hud-frenzy-wrap.is-warning{animation:none}}
/* The dog going. The one meter the errand has — there was a second, identically
   shaped one for the cleanup hold a couple of seconds later, and the pair read
   as one meter that mysteriously restarted. It is in the poop's own brown. */
.hud-poop{position:absolute;left:50%;bottom:56px;transform:translateX(-50%);
  width:230px;display:none;text-align:center}
.hud-poop .hud-bar{height:16px;border-radius:8px}
.hud-poop .hud-bar>i{background:#c79a5c;border-radius:8px}
.hud-poop-label{font-size:12px;margin-top:4px;opacity:.9}
/* The guided walk's card, in the top strip under the objective block.
   It sat bottom-centre to begin with, which is where a phone player's own thumb
   is: they hold the stick down there to walk, and the hand covers the sentence
   telling them to. The top of the screen is the one part of it no thumb is ever
   over. It is in flow under the objective block rather than at a top offset of
   its own, so it cannot land on that block when the objective wraps to three
   lines on a narrow screen; align-self centres it without taking it out of that
   flow. (No backticks in this block: the whole stylesheet is a template
   literal.)
   It stays pointer-events:none like the rest of the overlay: a drag that starts
   on it must still reach the stick underneath. */
.hud-tutorial{align-self:center;width:min(430px,88vw);box-sizing:border-box;
  display:none;text-align:center;
  background:rgba(18,24,30,.74);border-radius:12px;border-top:3px solid #8ecbff;
  padding:9px 14px 11px}
.hud-tutorial-step{font-size:11px;letter-spacing:.7px;text-transform:uppercase;opacity:.66}
.hud-tutorial-text{font-size:15px;line-height:1.32;margin-top:3px}
/* Only there while an aside is live, and in the same amber the urgent objective
   line uses — one colour for "this is the bit that will end your walk". */
.hud-tutorial-tip{font-size:13px;line-height:1.3;margin-top:7px;color:#ffd79a;display:none}
.hud-tutorial.is-new{animation:hud-card-in .34s ease-out}
/* Drops in from above now that it hangs off the top strip, rather than rising
   from the bottom of the screen. */
@keyframes hud-card-in{
  from{opacity:0;transform:translateY(-10px)}
  to{opacity:1;transform:translateY(0)}}
@media (prefers-reduced-motion:reduce){.hud-tutorial.is-new{animation:none}}
/* Only the end-of-run overlays take pointer events, and only while shown — so a
   tap anywhere restarts on a device with no R key. */
.hud-overlay{position:absolute;inset:0;background:rgba(0,0,0,.62);display:none;
  flex-direction:column;align-items:center;justify-content:center;gap:14px;
  pointer-events:auto;cursor:pointer}
.hud-overlay h1{margin:0;font-size:42px}
.hud-overlay p{margin:0;font-size:19px}
.hud-overlay button{border:0;border-radius:10px;padding:9px 18px;font:inherit;
  font-size:15px;color:#fff;background:rgba(255,255,255,.16);cursor:pointer}
.hud-overlay button:hover,.hud-overlay button:focus-visible{
  background:rgba(255,255,255,.28);outline:none}
.hud-next-btn{background:rgba(255,255,255,.32);font-weight:600}
.hud-next-btn:hover,.hud-next-btn:focus-visible{background:rgba(255,255,255,.45)}
.hud-win h1{color:#9dffb0}
.hud-lose h1{color:#ff8a8a}
.hud-home-pointer{position:fixed;pointer-events:none;z-index:20;display:none;
  transform:translate(-50%,-50%);align-items:center;gap:8px;
  background:rgba(18,38,24,.88);border:2px solid #9dffb0;border-radius:20px;
  padding:6px 12px;font-size:13px;font-weight:600;color:#9dffb0;
  box-shadow:0 4px 14px rgba(0,0,0,.6);white-space:nowrap}
.hud-pointer-arrow{font-size:16px;display:inline-block;line-height:1}
@media (max-width:640px){
  .hud-topleft{max-width:54vw}
  .hud-objective{font-size:14px}
  .hud-dog{font-size:13px}
  .hud-sound-btn{width:28px;height:28px;font-size:14px}
  /* Narrower meter so it can't run into the objective text beside it. */
  .hud-frenzy-wrap{width:104px}
  .hud-level{font-size:11px}
  .hud-overlay h1{font-size:30px}
  /* Stays centred on a phone. It used to be pushed into the left corner to
     clear the cleanup button on the right; with the button gone there is
     nothing down there to avoid. */
  .hud-poop{width:min(230px,60vw);bottom:46px}
  /* Wider on a phone: the strip is the full width, and the card is the thing
     being read. */
  .hud-tutorial{width:min(430px,94vw);padding:8px 12px 10px}
  .hud-tutorial-text{font-size:13px}
  .hud-tutorial-tip{font-size:12px}}
`;

export class Hud {
  private root: HTMLDivElement;
  private objectiveEl: HTMLDivElement;
  private levelEl: HTMLDivElement;
  private dogEl: HTMLDivElement;
  private frenzyWrap: HTMLDivElement;
  private frenzyFill: HTMLElement;
  private poopWrap: HTMLDivElement;
  private poopFill: HTMLElement;
  private tutorialWrap: HTMLDivElement;
  private tutorialStep: HTMLElement;
  private tutorialText: HTMLElement;
  private tutorialTip: HTMLElement;
  private winEl: HTMLDivElement;
  private loseEl: HTMLDivElement;
  private loseTitle: HTMLElement;
  private winHint: HTMLElement;
  private loseHint: HTMLElement;
  private soundBtn: HTMLButtonElement;
  private homePointerEl: HTMLDivElement;
  private pointerArrowEl: HTMLDivElement;
  private pointerDistEl: HTMLSpanElement;
  private styleEl: HTMLStyleElement;
  private subs: Array<[string, (...a: never[]) => void]> = [];

  /** Set by main.ts — fired when an end-of-run overlay is tapped. */
  onRestart?: () => void;

  /** Control scheme the wording currently reflects; updated live from the game. */
  private scheme: InputScheme = 'keys';
  /** Phase the objective line is showing, so it can be re-worded in place. */
  private objective: Objective = Objective.WALKING;
  /** Position of the player in world space for edge arrow calculations. */
  private playerPos: Vec2 = { x: 0, y: 0 };
  /** What the dog is doing, so the urge can take over the objective line. */
  private dogState: DogState = DogState.CALM;
  /** How the run ended, so the overlay names the right culprit. */
  private lossReason: LossReason = 'car';
  /** The guided walk's current lesson, kept so it can be re-worded in place. */
  private lesson: TutorialCard | null = null;

  private soundManager?: { isMuted: boolean; toggleMute: () => boolean };

  constructor(
    parent: HTMLElement,
    private events: Emitter,
    options: HudOptions = {},
  ) {
    this.soundManager = options.soundManager;
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);
    // Taken down again in dispose(): main.ts rebuilds the HUD on every restart,
    // so a stylesheet left behind would stack up one <style> per run.
    this.styleEl = style;

    this.root = document.createElement('div');
    this.root.className = 'hud';
    this.root.innerHTML = `
      <div class="hud-top">
        <div class="hud-topleft">
          <div class="hud-objective"></div>
          <div class="hud-dog">Dog: Calm</div>
          <div class="hud-level"></div>
        </div>
        <div class="hud-tutorial">
          <div class="hud-tutorial-step"></div>
          <div class="hud-tutorial-text"></div>
          <div class="hud-tutorial-tip"></div>
        </div>
      </div>
      <div class="hud-topright">
        <button class="hud-sound-btn" aria-label="Toggle sound" title="Toggle sound">🔊</button>
        <button class="hud-menu-btn" type="button">☰ Levels</button>
      </div>
      <div class="hud-frenzy-wrap">
        <div class="hud-bar"><i></i></div>
        <div class="hud-label">Frenzy</div>
      </div>
      <div class="hud-poop">
        <div class="hud-bar"><i></i></div>
        <div class="hud-poop-label">The dog is going…</div>
      </div>
      <div class="hud-overlay hud-win"><h1>🏠 Home sweet home!</h1><p></p></div>
      <div class="hud-overlay hud-lose"><h1></h1><p></p></div>
      <div class="hud-home-pointer">
        <div class="hud-pointer-arrow">▲</div>
        <div class="hud-pointer-label">🏠 Home <span class="hud-pointer-dist"></span></div>
      </div>
    `;
    parent.appendChild(this.root);

    const q = <T extends HTMLElement>(sel: string): T => this.root.querySelector(sel) as T;
    this.objectiveEl = q('.hud-objective');
    this.dogEl = q('.hud-dog');
    this.frenzyWrap = q('.hud-frenzy-wrap');
    this.frenzyFill = q('.hud-frenzy-wrap .hud-bar > i');
    this.poopWrap = q('.hud-poop');
    this.poopFill = q('.hud-poop .hud-bar > i');
    this.tutorialWrap = q('.hud-tutorial');
    this.tutorialStep = q('.hud-tutorial-step');
    this.tutorialText = q('.hud-tutorial-text');
    this.tutorialTip = q('.hud-tutorial-tip');
    this.winEl = q('.hud-win');
    this.loseEl = q('.hud-lose');
    this.loseTitle = q('.hud-lose h1');
    this.winHint = q('.hud-win p');
    this.loseHint = q('.hud-lose p');
    this.soundBtn = q('.hud-sound-btn');
    this.homePointerEl = q('.hud-home-pointer');
    this.pointerArrowEl = q('.hud-pointer-arrow');
    this.pointerDistEl = q('.hud-pointer-dist');
    this.levelEl = q('.hud-level');
    if (options.levelLabel) this.levelEl.textContent = options.levelLabel;
    this.applyWording(); // fill the text now that the elements exist

    if (options.onMenu) this.wireMenu(q('.hud-menu-btn'), options.onMenu);
    if (options.onNextLevel) this.wireNextLevel(options.onNextLevel, options.nextLevelDifficulty);

    this.refreshSound();

    this.soundBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.soundManager) {
        this.soundManager.toggleMute();
      }
      this.events.emit(GameEvents.SOUND_TOGGLE_MUTE);
      this.refreshSound();
    });

    this.subscribe(GameEvents.OBJECTIVE, (o) => this.onObjective(o as Objective));
    this.subscribe(GameEvents.PLAYER_POS, (pos) => {
      this.playerPos = pos as Vec2;
      this.updateHomePointer();
    });
    // Re-word in place when the player switches control scheme mid-run.
    this.subscribe(GameEvents.INPUT_SCHEME, (s) => {
      this.scheme = s as InputScheme;
      this.applyWording();
    });
    this.subscribe(GameEvents.DOG_STATE, (s) => this.onDogState(s as DogState));
    this.subscribe(GameEvents.POOP_PROGRESS, (r) => this.onPoopProgress(r as number));
    this.subscribe(GameEvents.TUTORIAL, (c) => this.onTutorial(c as TutorialCard | null));
    this.subscribe(GameEvents.FRENZY, (f) => this.onFrenzy(f as number));
    this.subscribe(GameEvents.WON, () => {
      this.winEl.style.display = 'flex';
    });
    this.subscribe(GameEvents.LOST, (reason) => {
      // Defaults to the car for anything that ends the run without saying why.
      this.lossReason = (reason as LossReason) ?? 'car';
      this.applyWording();
      this.loseEl.style.display = 'flex';
    });

    // Tapping/clicking either end-of-run overlay restarts, so the run can be
    // reset without an R key.
    const tapRestart = (): void => this.onRestart?.();
    for (const overlay of [this.winEl, this.loseEl]) {
      overlay.addEventListener('click', tapRestart);
      this.overlayTaps.push(() => overlay.removeEventListener('click', tapRestart));
    }
  }

  private overlayTaps: Array<() => void> = [];

  /**
   * Show the way back to the picker: a corner button during play, and one on
   * each end-of-run overlay.
   *
   * Both buttons have to swallow **pointerdown as well as click**, and that is
   * the fiddly part. `core/Input.ts` starts the virtual walking stick on
   * pointerdown anywhere below `TOUCH.ZONE_TOP_FRACTION` and calls
   * `setPointerCapture`; once the pointer is captured, the browser dispatches
   * the following `click` to the capture target rather than to whatever is
   * under the finger, so the button never hears it. That is why the corner
   * button appeared to work (it sits inside the top dead zone) while the one on
   * the end-of-run overlay did nothing at all — and on a phone, pressing it
   * would have set the walker off instead. `touchControls.ts` guards its own
   * action button exactly this way.
   *
   * The click handler additionally stops propagation because the overlay
   * restarts on any tap — that's what makes a run resettable without a
   * keyboard — so without it "Levels" would restart the level *and* open the
   * menu, leaving a game running behind it.
   */
  /**
   * Redraw the sound button from the current mute setting.
   *
   * Public because the button is no longer the only thing that can change it: a
   * cloud sync brings the setting down from the account mid-run, and the icon
   * would otherwise sit there claiming the opposite until the next restart.
   */
  refreshSound(): void {
    const isMuted = this.soundManager ? this.soundManager.isMuted : false;
    this.soundBtn.textContent = isMuted ? '🔇' : '🔊';
    this.soundBtn.setAttribute('aria-label', isMuted ? 'Unmute sound' : 'Mute sound');
  }

  private wireMenu(button: HTMLButtonElement, onMenu: () => void): void {
    const open = (e: Event): void => {
      e.stopPropagation();
      onMenu();
    };
    // Keep the press away from the game's pointer handling; see above.
    const swallow = (e: Event): void => e.stopPropagation();

    const wire = (el: HTMLElement): void => {
      el.addEventListener('click', open);
      el.addEventListener('pointerdown', swallow);
      this.overlayTaps.push(() => {
        el.removeEventListener('click', open);
        el.removeEventListener('pointerdown', swallow);
      });
    };

    button.style.display = 'block';
    wire(button);

    for (const overlay of [this.winEl, this.loseEl]) {
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'hud-back';
      back.textContent = '☰ Levels';
      overlay.appendChild(back);
      wire(back);
    }
  }

  private wireNextLevel(onNextLevel: () => void, difficulty?: string): void {
    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'hud-next-btn';
    nextBtn.textContent = difficulty ? `Next Level (${difficulty})` : 'Next Level';

    const next = (e: Event): void => {
      e.stopPropagation();
      onNextLevel();
    };
    const swallow = (e: Event): void => e.stopPropagation();

    nextBtn.addEventListener('click', next);
    nextBtn.addEventListener('pointerdown', swallow);
    this.winEl.appendChild(nextBtn);
    this.overlayTaps.push(() => {
      nextBtn.removeEventListener('click', next);
      nextBtn.removeEventListener('pointerdown', swallow);
    });
  }

  private subscribe(event: string, fn: (...args: never[]) => void): void {
    this.events.on(event, fn);
    this.subs.push([event, fn]);
  }

  private onObjective(objective: Objective): void {
    this.objective = objective;
    if (objective !== Objective.WON) this.applyWording();
    this.updateHomePointer();
  }

  private updateHomePointer(): void {
    if (this.objective !== Objective.GO_HOME) {
      this.homePointerEl.style.display = 'none';
      return;
    }
    const doorstep = homeDoorstep(activeLevel().home);
    const arrow = computeEdgeArrow(
      this.playerPos,
      doorstep,
      window.innerWidth,
      window.innerHeight,
      42,
    );
    this.homePointerEl.style.display = 'flex';
    this.homePointerEl.style.left = `${arrow.x}px`;
    this.homePointerEl.style.top = `${arrow.y}px`;
    // `rotationDeg`, not `angleDeg`: the glyph rests pointing up and `angleDeg`
    // is measured from screen-right. See `EdgeArrowState.rotationDeg`.
    this.pointerArrowEl.style.transform = `rotate(${arrow.rotationDeg}deg)`;
    // `arrow.distance` is world units; the readout is metres. See
    // `formatDistance` — the two are not the same number.
    this.pointerDistEl.textContent = formatDistance(arrow.distance);
  }

  /** Re-word every hint for the control scheme now in use. */
  private applyWording(): void {
    // The urge outranks the free-roam line, and only that one: once the bag is
    // in hand the objective chain has something specific to say, and the dog's
    // own line underneath carries the rest.
    const urge = this.objective === Objective.WALKING ? URGE_TEXT[this.dogState] : undefined;
    this.objectiveEl.classList.toggle('is-urgent', urge !== undefined);
    this.objectiveEl.textContent = urge ?? OBJECTIVE_TEXT[this.objective][this.scheme];
    const hint = RESTART_HINT[this.scheme];
    const loss = LOSS_TEXT[this.lossReason];
    this.winHint.textContent = `${hint} walk again`;
    this.loseTitle.textContent = loss.title;
    this.loseHint.textContent = `${hint} ${loss.hint}`;
    this.drawTutorial();
  }

  /**
   * Show the guided walk's current lesson.
   *
   * The card is animated on arrival rather than on every redraw: a control
   * scheme changing mid-lesson re-words the same card, and a panel that jumped
   * every time somebody touched the screen would read as a new instruction each
   * time.
   */
  private onTutorial(card: TutorialCard | null): void {
    const fresh = card !== null && card.step !== this.lesson?.step;
    this.lesson = card;
    this.drawTutorial();
    if (!fresh) return;
    // Restart the entrance animation: removing the class does nothing until the
    // browser has recomputed style, which reading offsetWidth forces.
    this.tutorialWrap.classList.remove('is-new');
    void this.tutorialWrap.offsetWidth;
    this.tutorialWrap.classList.add('is-new');
  }

  /** Redraw the tutorial card from `this.lesson` and the scheme in use. */
  private drawTutorial(): void {
    const card = this.lesson;
    this.tutorialWrap.style.display = card ? 'block' : 'none';
    if (!card) return;
    this.tutorialStep.textContent = `Step ${card.number} of ${card.total}`;
    this.tutorialText.textContent = TUTORIAL_TEXT[card.step][this.scheme];
    this.tutorialTip.style.display = card.tip ? 'block' : 'none';
    this.tutorialTip.textContent = card.tip ? TUTORIAL_TIP_TEXT[card.tip] : '';
  }

  private onDogState(state: DogState): void {
    const info = DOG_STATE_INFO[state];
    this.dogEl.textContent = `Dog: ${info.label}`;
    this.dogEl.style.color = info.color;
    this.dogState = state;
    this.applyWording(); // the urge states speak through the objective line too
  }

  /** The dog's squat, as a meter that only exists while it's going. */
  private onPoopProgress(ratio: number): void {
    this.poopWrap.style.display = ratio > 0 ? 'block' : 'none';
    this.poopFill.style.width = `${Math.round(ratio * 100)}%`;
  }

  private onFrenzy(ratio: number): void {
    this.frenzyFill.style.width = `${Math.round(ratio * 100)}%`;
    // Green → red as the dog winds up.
    const r = Math.round(0x4f + (0xd9 - 0x4f) * ratio);
    const g = Math.round(0xd1 + (0x4f - 0xd1) * ratio);
    const b = Math.round(0x8b + (0x4f - 0x8b) * ratio);
    this.frenzyFill.style.background = `rgb(${r},${g},${b})`;
    // Past the warning mark the whole meter flashes: colour alone is the one
    // channel a player can be missing, and this one ends the run.
    this.frenzyWrap.classList.toggle('is-warning', ratio >= FRENZY.WARN_AT);
  }

  /** Reset all transient UI back to a fresh run. */
  reset(): void {
    this.winEl.style.display = 'none';
    this.loseEl.style.display = 'none';
    this.onPoopProgress(0);
    this.onFrenzy(0);
    this.lossReason = 'car';
    // The guided walk starts over with the run; the game re-announces its first
    // lesson from `emitInitialState`.
    this.lesson = null;
    this.onObjective(Objective.WALKING);
    this.onDogState(DogState.CALM);
  }

  dispose(): void {
    for (const [event, fn] of this.subs) this.events.off(event, fn);
    this.subs = [];
    for (const off of this.overlayTaps) off();
    this.overlayTaps = [];
    this.root.remove();
    this.styleEl.remove();
  }
}
